import { DashboardDateSelectionContext } from "../dashboard/DashboardDateSelectionContext";
import { readDateSelection } from "../dashboard/dateSelection";
import { getBrowserHash } from "../../utils/urlHash";
import { processDetailHref } from "./processDetailHref";
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import clsx from "clsx";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Star,
  type LucideIcon,
  UserRound,
} from "lucide-react";
import React, { Fragment, useContext, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import type { ProcessSummary } from "../../api/types";
import { formatDuration } from "../../utils";
import { appPrimaryColor, getPersonColor, personInitials } from "../analytics/palette";
import AppUsagePreview, { AppIcon } from "../AppUsagePreview";
import { useHoverPin } from "../useHoverPin";
import {
  type DirectoryGroup,
  type PersonProcesses,
  type ProcessGrouping,
  DEFAULT_PROCESS_SORT,
  buildDirectoryGroups,
  frequentMinimumRuns,
  groupingContext,
  processTotalDuration,
  teamTimeShare,
} from "./directoryGrouping";
import { type Categories, PROCESS_DESCRIPTION_PLACEHOLDERS } from "./processGroups";
import { personSeconds, processPeople } from "./processPeople";
import {
  type GroupingColumn,
  PROCESS_COLUMNS,
  type ProcessColumn,
  isGroupingColumn,
} from "./processPreferences";

type ColumnSpec = {
  width: number;
  align?: "center" | "right";
  header: string;
  icon?: LucideIcon;
  title?: string;
  className?: string;
  // The directory sort this header applies when clicked.
  sort?: string;
  cell: (process: ProcessSummary, group?: DirectoryGroup) => React.ReactNode;
};
const ALIGN = { center: "text-center", right: "text-right" } as const;
const PROCESS_COLUMN_MIN_WIDTH = 324;
const OPEN_COLUMN_WIDTH = 36;

export function DirectoryItems({
  label,
  children,
  content,
}: {
  label: string;
  children: React.ReactNode;
  content: React.ReactNode;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const popupId = useId();
  // Previews on hover, like the apps beside it, and pins on click.
  const hover = useHoverPin(500);
  return (
    <Popover.Root open={hover.open} onOpenChange={hover.onOpenChange}>
      <Popover.Anchor asChild>
        <button
          ref={anchor}
          type="button"
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={hover.open}
          aria-controls={hover.open ? popupId : undefined}
          onPointerEnter={() => hover.show(true)}
          onPointerLeave={hover.leave}
          onFocus={() => hover.show()}
          onBlur={hover.leave}
          onClick={hover.togglePin}
          className="inline-flex max-w-full cursor-pointer flex-wrap items-center gap-1 rounded-md py-1 text-text-mid outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30"
        >
          {children}
        </button>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          id={popupId}
          aria-label={label}
          side="bottom"
          align="start"
          sideOffset={6}
          {...hover.contentProps}
          onInteractOutside={event => {
            // The trigger toggles the pin itself; dismissing first would reopen it.
            if (anchor.current?.contains(event.target as Node)) event.preventDefault();
          }}
          className="z-50 max-h-64 w-64 overflow-y-auto rounded-xl border border-outline bg-surface p-3 text-sm text-text shadow-lg"
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default function ProcessDirectoryTable({
  processes,
  scopeProcesses = processes,
  grouping,
  personProcesses = {},
  sort = DEFAULT_PROCESS_SORT,
  onSortChange,
  periodDays = 1,
  columns,
  showPeople,
  userOptions,
  activeUserIds,
  favouriteSlugs,
  onToggleFavourite,
  categories,
}: {
  processes: ProcessSummary[];
  scopeProcesses?: ProcessSummary[];
  grouping: ProcessGrouping;
  personProcesses?: PersonProcesses;
  sort?: string;
  onSortChange?: (sort: string) => void;
  periodDays?: number;
  columns: readonly ProcessColumn[];
  showPeople: boolean;
  userOptions: Array<{ id: string; label: string }>;
  activeUserIds: string[];
  favouriteSlugs: ReadonlySet<string>;
  onToggleFavourite: (slug: string) => void;
  categories?: Categories;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selection: rememberedDates } = useContext(DashboardDateSelectionContext);
  const dates = readDateSelection(getBrowserHash(), rememberedDates);
  const grouped = grouping !== "none";
  const groups = buildDirectoryGroups(
    processes,
    grouping,
    userOptions,
    personProcesses,
    sort,
    periodDays,
    favouriteSlugs,
    categories,
  );
  // Default from the current data, so asynchronously arriving groups also open only the first.
  const [openOverrides, setOpenOverrides] = useState<Map<string, boolean>>(new Map());
  const isOpen = (id: string) => openOverrides.get(id) ?? groups[0]?.id === id;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const personGrouping = grouping === "person";
  const allPeopleSelected =
    userOptions.length > 0 &&
    activeUserIds.length === userOptions.length &&
    userOptions.every(user => activeUserIds.includes(user.id));
  const scopeBySlug = new Map(scopeProcesses.map(process => [process.slug, process]));
  const date = (value: string) => {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime())
      ? new Intl.DateTimeFormat(i18n?.resolvedLanguage, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(parsed)
      : "—";
  };
  const names = new Intl.Collator(i18n?.resolvedLanguage, {
    sensitivity: "base",
    numeric: true,
  });
  const unavailable = (
    <span className="text-text-dim" title={t("process_directory.unavailable")}>
      —
    </span>
  );
  const metric = "whitespace-nowrap text-sm text-text-mid tabular-nums";
  const specs: Record<Exclude<ProcessColumn, GroupingColumn>, ColumnSpec> = {
    people: {
      width: 112,
      header: t("process_directory.people"),
      sort: "shared",
      cell: process => {
        const people: Array<{ id: string; label: string; seconds: number }> = processPeople(process)
          .map(person => ({
            id: person.userId,
            label:
              userOptions.find(user => user.id === person.userId)?.label ??
              t("process_directory.unknown_person"),
            seconds: personSeconds(person),
          }))
          .sort(
            (a, b) =>
              b.seconds - a.seconds || names.compare(a.label, b.label) || a.id.localeCompare(b.id),
          );
        if (!people.length) return unavailable;
        return (
          <DirectoryItems
            label={`${t("process_directory.people")}: ${people.map(p => p.label).join(", ")}`}
            content={
              <ul className="space-y-2">
                {people.map(p => (
                  <li key={p.id} className="flex items-center gap-2">
                    <UserRound aria-hidden="true" className="h-4 w-4 shrink-0 text-info" />
                    <span className="min-w-0 flex-1">{p.label}</span>
                    <span className="shrink-0 text-xs text-text-dim tabular-nums">
                      {formatDuration(p.seconds)}
                    </span>
                  </li>
                ))}
              </ul>
            }
          >
            <span className="flex gap-1" aria-hidden="true">
              {people.slice(0, 2).map(person => (
                <span
                  key={person.id}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-info/10 text-xs font-semibold text-info"
                >
                  {person.label
                    .split(" ")
                    .slice(0, 2)
                    .map(word => word[0])
                    .join("")}
                </span>
              ))}
            </span>
            {people.length > 2 && (
              <span className="text-xs tabular-nums">+{people.length - 2}</span>
            )}
          </DirectoryItems>
        );
      },
    },
    tools: {
      width: 144,
      header: t("process_directory.tools"),
      cell: process => {
        const apps = [...(process.apps ?? [])].sort(
          (a, b) =>
            (process.appSeconds?.[b] ?? 0) - (process.appSeconds?.[a] ?? 0) || names.compare(a, b),
        );
        return apps.length ? (
          <AppUsagePreview apps={apps} appSeconds={process.appSeconds} />
        ) : (
          unavailable
        );
      },
    },
    cases: {
      width: 72,
      align: "center",
      header: t("process_directory.instances"),
      className: "whitespace-nowrap text-sm font-medium tabular-nums",
      sort: "frequency",
      cell: process => process.frequency,
    },
    avgDuration: {
      width: 112,
      align: "right",
      header: t("process_directory.avg_duration"),
      className: metric,
      sort: "duration",
      cell: process => formatDuration(process.avgDuration),
    },
    medianDuration: {
      width: 128,
      align: "right",
      header: t("process_directory.median_duration"),
      className: metric,
      cell: process =>
        process.medianDuration === undefined ? unavailable : formatDuration(process.medianDuration),
    },
    totalTime: {
      width: 104,
      align: "right",
      header: t("process_directory.total_time"),
      title: t("process_directory.total_time_hint"),
      className: metric,
      sort: "total",
      cell: process => formatDuration(processTotalDuration(process)),
    },
    steps: {
      width: 96,
      align: "right",
      header: t("process_directory.avg_steps"),
      title: t("steps_per_instance_full"),
      className: metric,
      cell: process =>
        process.frequency ? Math.round(process.stepCount / process.frequency) : unavailable,
    },
    lastObserved: {
      width: 136,
      header: t("process_directory.last_observed"),
      className: "whitespace-nowrap text-sm text-text-mid",
      sort: "recent",
      cell: process => date(process.lastSeen),
    },
  };
  const percent = (share: number) =>
    share > 0 && share < 0.005
      ? "<1%"
      : new Intl.NumberFormat(i18n?.resolvedLanguage, {
          style: "percent",
          maximumFractionDigits: 0,
        }).format(share);
  const context = groupingContext(grouping);
  // The grouping's own measure. Both are shares of time, so PROCESS_COLUMNS places it just
  // before Total time (or where Total time would be) to read against it.
  const contextSpec: ColumnSpec | undefined = context && {
    width: personGrouping ? 136 : 152,
    align: "right",
    header: t(context.label),
    icon: context.icon,
    title: t(context.hint),
    className: metric,
    sort: context.sort,
    cell: (process, group) => {
      const duration = formatDuration(processTotalDuration(process));
      if (personGrouping) {
        const team = scopeBySlug.get(process.slug);
        const share = teamTimeShare(process, team);
        return share === undefined || !team ? (
          unavailable
        ) : (
          <span
            title={t("process_directory.share_of_team_detail", {
              duration,
              total: formatDuration(processTotalDuration(team)),
            })}
          >
            {percent(share)}
          </span>
        );
      }
      const appDuration = group?.app ? process.appSeconds?.[group.app] : undefined;
      if (appDuration === undefined || !group?.app) return unavailable;
      const total = processTotalDuration(process);
      return (
        <span
          title={t("process_directory.time_in_app_detail", {
            duration: formatDuration(appDuration),
            app: group.app,
            total: duration,
          })}
        >
          {formatDuration(appDuration)}
          {total > 0 && <span className="text-text-dim"> ({percent(appDuration / total)})</span>}
        </span>
      );
    },
  };
  const shown = PROCESS_COLUMNS.flatMap((column): Array<ColumnSpec & { id: ProcessColumn }> => {
    if (!columns.includes(column)) return [];
    if (isGroupingColumn(column))
      return contextSpec && context?.column === column ? [{ id: column, ...contextSpec }] : [];
    return column !== "people" || showPeople ? [{ id: column, ...specs[column] }] : [];
  });
  // The process name, each shown metric, and the trailing open-process chevron.
  const columnCount = shown.length + 2;
  const minWidth =
    PROCESS_COLUMN_MIN_WIDTH + OPEN_COLUMN_WIDTH + shown.reduce((sum, c) => sum + c.width, 0);
  // Every sort is in its natural direction: A–Z for names, largest or latest first otherwise.
  const ascending = (key: string) => key === "name";
  const ariaSort = (key?: string) =>
    key && key === sort ? (ascending(key) ? "ascending" : "descending") : undefined;
  const sortableHeader = (label: React.ReactNode, key?: string) =>
    key && onSortChange ? (
      <button
        type="button"
        onClick={() => onSortChange(key)}
        className={clsx(
          "inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded outline-none hover:text-text focus-visible:ring-2 focus-visible:ring-info/30",
          key === sort && "text-text",
        )}
      >
        {label}
        {key === sort &&
          (ascending(key) ? (
            <ArrowUp aria-hidden="true" className="h-3 w-3" />
          ) : (
            <ArrowDown aria-hidden="true" className="h-3 w-3" />
          ))}
      </button>
    ) : (
      label
    );
  const row = (process: ProcessSummary, group?: DirectoryGroup) => {
    const userIds = group?.personId ? [group.personId] : allPeopleSelected ? "all" : activeUserIds;
    const description =
      PROCESS_DESCRIPTION_PLACEHOLDERS[process.slug] ??
      t("process_directory.placeholder_description");
    const favourite = favouriteSlugs.has(process.slug);
    return (
      <tr
        key={process.slug}
        data-tour="process-card"
        data-process-slug={process.slug}
        onClick={event => {
          if (
            (event.target as HTMLElement).closest("a, button, input, [tabindex], [role='dialog']")
          )
            return;
          if (window.getSelection()?.toString()) return;
          navigate(processDetailHref(process.slug, userIds, dates));
        }}
        className="group cursor-pointer border-b border-outline/70 last:border-0 hover:bg-info/5"
      >
        <td className="py-3 pl-4 pr-4 align-middle">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              aria-pressed={favourite}
              aria-label={t(
                favourite ? "remove_process_from_favourites" : "add_process_to_favourites",
                { name: process.name },
              )}
              onClick={() => onToggleFavourite(process.slug)}
              className="shrink-0 cursor-pointer rounded p-1 text-text-dim outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30"
            >
              <Star
                aria-hidden="true"
                className={clsx("h-4 w-4", favourite && "fill-info/70 text-info")}
              />
            </button>
            <div className="min-w-0">
              <Link
                to={processDetailHref(process.slug, userIds, dates)}
                className="block rounded text-base font-medium leading-6 text-text outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30"
              >
                {process.name}
              </Link>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <p
                    tabIndex={0}
                    className="mt-0.5 truncate rounded text-sm leading-5 text-text-mid outline-none focus-visible:ring-2 focus-visible:ring-info/30"
                  >
                    {description}
                  </p>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    collisionPadding={12}
                    className="z-50 max-w-sm rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text shadow-lg"
                  >
                    {description}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </div>
          </div>
        </td>
        {shown.map(column => (
          <td
            key={column.id}
            className={clsx("px-3 py-3", column.align && ALIGN[column.align], column.className)}
          >
            {column.cell(process, group)}
          </td>
        ))}
        <td className="pr-3">
          <Link
            to={processDetailHref(process.slug, userIds, dates)}
            aria-label={t("process_directory.open_process", {
              name: process.name,
            })}
            className="inline-flex rounded p-1 text-text-dim outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </Link>
        </td>
      </tr>
    );
  };
  return (
    <Tooltip.Provider delayDuration={150}>
      <div className="@container overflow-x-auto rounded-xl border border-outline bg-surface">
        <table
          className="w-full table-fixed border-collapse text-left text-sm"
          style={{ minWidth }}
        >
          <colgroup>
            <col />
            {shown.map(column => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
            <col style={{ width: OPEN_COLUMN_WIDTH }} />
          </colgroup>
          <thead className="border-b border-outline bg-muted-surface/60 text-xs font-medium text-text-dim">
            <tr>
              <th scope="col" aria-sort={ariaSort("name")} className="py-3 pl-14 pr-4">
                {sortableHeader(t("process_directory.process"), "name")}
              </th>
              {shown.map(column => (
                <th
                  key={column.id}
                  scope="col"
                  title={column.title}
                  aria-sort={ariaSort(column.sort)}
                  className={clsx("px-3 py-3", column.align && ALIGN[column.align])}
                >
                  {sortableHeader(
                    column.icon ? (
                      <>
                        <column.icon aria-hidden="true" className="h-3.5 w-3.5" />
                        {column.header}
                      </>
                    ) : (
                      column.header
                    ),
                    column.sort,
                  )}
                </th>
              ))}
              <th scope="col">
                <span className="sr-only">{t("process_directory.open")}</span>
              </th>
            </tr>
          </thead>
          {grouped ? (
            groups.map(group => {
              const accent = group.personId
                ? getPersonColor(group.personId)
                : group.app
                  ? appPrimaryColor(group.app)
                  : undefined;
              return (
                <tbody key={group.id}>
                  <tr className="border-y border-outline">
                    <th colSpan={columnCount} scope="rowgroup" className="p-0 font-normal">
                      <button
                        type="button"
                        aria-expanded={isOpen(group.id)}
                        onClick={() =>
                          setOpenOverrides(current =>
                            new Map(current).set(group.id, !isOpen(group.id)),
                          )
                        }
                        className={clsx(
                          "flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info/50",
                          accent ? "text-text-mid" : group.tone,
                        )}
                        style={
                          accent
                            ? {
                                backgroundColor: group.personId
                                  ? getPersonColor(group.personId, 0.09)
                                  : `${accent}18`,
                              }
                            : undefined
                        }
                      >
                        <span
                          aria-hidden="true"
                          className={clsx(
                            "flex h-8 w-8 shrink-0 items-center justify-center",
                            group.personId
                              ? "rounded-full text-xs font-semibold text-white"
                              : "rounded-lg",
                            !group.personId && (group.app ? "text-text-mid" : group.tone),
                          )}
                          style={
                            accent
                              ? { backgroundColor: group.personId ? accent : `${accent}20` }
                              : undefined
                          }
                        >
                          {group.personId ? (
                            personInitials(group.name, i18n?.resolvedLanguage)
                          ) : group.app ? (
                            <AppIcon name={group.app} />
                          ) : (
                            <group.icon aria-hidden="true" className="h-5 w-5" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-base font-semibold text-text">
                            {group.bucket
                              ? t(`process_directory.bucket_${group.bucket}`, {
                                  minimum: frequentMinimumRuns(periodDays),
                                  maximum: frequentMinimumRuns(periodDays) - 1,
                                })
                              : group.name ||
                                t(
                                  grouping === "application"
                                    ? "process_directory.unknown_application"
                                    : "process_directory.unknown_category",
                                )}
                          </span>
                          {(group.bucket === "long" ||
                            group.bucket === "short" ||
                            group.bucket === "unclassified") && (
                            <span className="mt-1 block text-sm leading-5 text-text-mid">
                              {t(`process_directory.${group.bucket}_hint`)}
                            </span>
                          )}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-outline bg-surface/80 px-3 py-1.5 text-sm font-medium text-text">
                          <span>
                            {t("process_directory.group_count", {
                              count: group.processes.length,
                            })}
                          </span>
                          <span aria-hidden="true" className="h-3 w-px bg-outline" />
                          <span>
                            {group.totalDuration === null
                              ? t("process_directory.time_unavailable")
                              : t(
                                  group.app
                                    ? "process_directory.group_app_duration"
                                    : "process_directory.group_total_duration",
                                  {
                                    duration: formatDuration(group.totalDuration),
                                    app: group.app,
                                  },
                                )}
                          </span>
                        </span>
                        {!isOpen(group.id) ? (
                          <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0" />
                        ) : (
                          <ChevronUp aria-hidden="true" className="h-4 w-4 shrink-0" />
                        )}
                      </button>
                    </th>
                  </tr>
                  {isOpen(group.id) && (
                    <Fragment>
                      {(expanded.has(group.id) ? group.processes : group.processes.slice(0, 4)).map(
                        process => row(process, group),
                      )}
                      {group.processes.length > 4 && (
                        <tr className="border-b border-outline">
                          <td colSpan={columnCount} className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => toggleExpanded(group.id)}
                              className="inline-flex cursor-pointer items-center gap-2 rounded py-1 text-sm font-medium text-info outline-none focus-visible:ring-2 focus-visible:ring-info/30"
                            >
                              {expanded.has(group.id) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                              {expanded.has(group.id)
                                ? t("process_directory.show_less")
                                : t("process_directory.show_more", {
                                    count: group.processes.length - 4,
                                  })}
                            </button>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )}
                </tbody>
              );
            })
          ) : (
            <tbody>{processes.map(process => row(process))}</tbody>
          )}
        </table>
      </div>
    </Tooltip.Provider>
  );
}
